/* eslint-disable react/no-array-index-key */
import type { ServiceLine } from 'api/types/service.types';
import { useConfig } from 'context/app-config';
import DriverInstructionsOptions from 'packages/careem/newOrder/components/DriverInstructionsOption';
import GetTheBagsOptionWithSheet from 'packages/careem/newOrder/components/GetTheBagsOptionWithSheet';
import SelectAddress from 'packages/shared/newOrder/components/SelectAddress';
import SelectPickupOrDropoff from 'packages/shared/newOrder/components/SelectPickupOrDropoff';
import { selectCurrentOrder, selectSelectedServices, selectServiceLineDropOffs } from 'pages/newOrder/newOrder.selectors';
import { usePickupTimeSlots } from 'pages/queries/timeslot.queries';
import React, { useCallback, useMemo } from 'react';
import ListGroup from 'react-bootstrap/ListGroup';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import ServiceLineBasedDropoff from './ServiceLineBasedDropoff';

export const AddressSheetContext = React.createContext({ toggleSheet: () => {} });

export const useAddressSheet = () => React.useContext(AddressSheetContext);

export const PickupDropoffOptions: React.FC = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const theme = useTheme();
  const selectedServices = useSelector(selectSelectedServices);
  const hasSupportForMultipleDropoffs = config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs;
  const serviceLineDropoffs = useSelector(selectServiceLineDropOffs);
  const { addressId, pickupTimeslot, dropoffTimeslot } = useSelector(selectCurrentOrder);

  const { data: pickupTimeSlots, fetchStatus: pickUpFetchStatus } = usePickupTimeSlots({
    addressId,
    enabled: Boolean(addressId && !pickupTimeslot),
  });

  const hasInformationSelected = useMemo(() => {
    if (hasSupportForMultipleDropoffs) {
      return selectedServices.some((service) => Boolean(serviceLineDropoffs[service]) !== true) || !pickupTimeslot;
    }

    return !(pickupTimeslot && dropoffTimeslot);
  }, [dropoffTimeslot, hasSupportForMultipleDropoffs, pickupTimeslot, selectedServices, serviceLineDropoffs]);

  const doesShowDelayDescription = useCallback((serviceLine: ServiceLine) => {
    if (!theme.components.selectPickupOrDropOffItem?.hideDelayDescription) {
      return true;
    }

    return selectedServices.length === 1 && !theme.components.selectPickupOrDropOffItem?.hideDelayDescription[serviceLine];
  }, []);

  const DropOffComponents = useMemo(
    () => (
      <>
        <SelectPickupOrDropoff isLoading={pickUpFetchStatus === 'fetching'} timeSlotsData={pickupTimeSlots?.pickupTimeslotsList} />
        {hasSupportForMultipleDropoffs ? (
          <>
            {selectedServices.map((serviceLine, index) => (
              <ServiceLineBasedDropoff
                key={`${serviceLine}select-pickup-dropoff`}
                action="DROPOFF"
                description={
                  doesShowDelayDescription(serviceLine) && t(`newOrderPage.dropOffDescription.delayMessage${serviceLine}`)
                    ? `newOrderPage.dropOffDescription.delayMessage${serviceLine}`
                    : ''
                }
                serviceLine={serviceLine}
                serviceLineDependancy={selectedServices[index - 1]}
              />
            ))}
          </>
        ) : (
          <ServiceLineBasedDropoff action="DROPOFF" description="newOrderPage.dropOffDescription.delayMessage" />
        )}
        {config?.showDriverInstructionsOnPickupDropoff && <DriverInstructionsOptions isDisabled={hasInformationSelected} />}
        {config?.showGetBagsMessageOnPickupDropoff && <GetTheBagsOptionWithSheet />}
      </>
    ),
    [
      config?.showDriverInstructionsOnPickupDropoff,
      config?.showGetBagsMessageOnPickupDropoff,
      hasInformationSelected,
      hasSupportForMultipleDropoffs,
      pickUpFetchStatus,
      pickupTimeSlots?.pickupTimeslotsList,
      selectedServices,
    ]
  );

  const Options: JSX.Element[] = useMemo(() => [<SelectAddress />, DropOffComponents], [DropOffComponents]);

  return (
    <ListGroup className="border-bottom-0" variant="flush">
      {Options.map((el, index) => React.cloneElement(el, { key: index }))}
    </ListGroup>
  );
};

export default PickupDropoffOptions;
